import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import MasterLayout from "../masterLayout/MasterLayout";

const GHL_BASE_URL = "https://services.leadconnectorhq.com";
const GHL_API_VERSION = "v3";

const EditCustomerPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [customerData, setCustomerData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    address1: "",
    city: "",
    state: "",
    postalCode: "",
    country: "",
  });

  const [errors, setErrors] = useState({});

  const [message, setMessage] = useState({
    status: false,
    reason: "",
    desc: "",
  });

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  /*
   * ==========================================
   * GET TOKEN
   * ==========================================
   */
  const getToken = () => {
    return localStorage.getItem("tokenID");
  };

  /*
   * ==========================================
   * FETCH SINGLE GHL CUSTOMER
   * GET /contacts/:contactId
   * ==========================================
   */
  const fetchCustomer = async () => {
    const token = getToken();

    if (!id) {
      setMessage({
        status: true,
        reason: "error",
        desc: "Contact ID is missing.",
      });

      setLoading(false);
      return;
    }

    if (!token) {
      setMessage({
        status: true,
        reason: "error",
        desc: "GHL access token not found.",
      });

      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      const response = await axios.get(
        `${GHL_BASE_URL}/contacts/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Version: GHL_API_VERSION,
            Accept: "application/json",
          },
        }
      );
      const contact = response.data?.contact;

      if (!contact) {
        throw new Error("Contact information not found.");
      }

      setCustomerData({
        firstName: contact.firstName || "",
        lastName: contact.lastName || "",
        email: contact.email || "",
        phone: contact.phone || "",
        address1: contact.address1 || "",
        city: contact.city || "",
        state: contact.state || "",
        postalCode: contact.postalCode || "",
        country: contact.country || "",
      });
    } catch (error) {
      console.error(
        "Error fetching GHL customer:",
        error.response?.data || error
      );

      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to fetch customer.";

      setMessage({
        status: true,
        reason: "error",
        desc: `Failed to fetch customer: ${errorMessage}`,
      });
    } finally {
      setLoading(false);
    }
  };

  /*
   * ==========================================
   * FETCH CUSTOMER WHEN PAGE LOADS
   * ==========================================
   */
  useEffect(() => {
    fetchCustomer();
  }, [id]);

  /*
   * ==========================================
   * HANDLE INPUT
   * ==========================================
   */
  const handleInputChange = (field, value) => {
    setCustomerData((prev) => ({
      ...prev,
      [field]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [field]: "",
    }));
  };

  /*
   * ==========================================
   * VALIDATION
   * ==========================================
   */
  const validateForm = () => {
    const newErrors = {};

    if (!customerData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (
      customerData.email &&
      !/\S+@\S+\.\S+/.test(customerData.email)
    ) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!customerData.phone.trim()) {
      newErrors.phone = "Phone number is required";
    }

    if (
      customerData.country &&
      customerData.country.length !== 2
    ) {
      newErrors.country =
        "Country should be a 2-letter code, e.g. PK, US, GB";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /*
   * ==========================================
   * UPDATE GHL CUSTOMER
   * PUT /contacts/:contactId
   * ==========================================
   */
  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    const token = getToken();

    if (!token) {
      setMessage({
        status: true,
        reason: "error",
        desc: "GHL access token not found.",
      });

      return;
    }

    try {
      setIsSubmitting(true);

      setMessage({
        status: false,
        reason: "",
        desc: "",
      });

      const fullName = `${customerData.firstName} ${customerData.lastName}`
        .replace(/\s+/g, " ")
        .trim();

      /*
       * Only send fields we actually want to update.
       *
       * IMPORTANT:
       * Don't send tags/customFields unless you're editing them,
       * because GHL can overwrite existing tags when tags are sent.
       */
      const payload = {
        firstName: customerData.firstName.trim(),
        lastName: customerData.lastName.trim(),
        name: fullName,
        email: customerData.email.trim(),
        phone: customerData.phone.trim(),
        address1: customerData.address1.trim(),
        city: customerData.city.trim(),
        state: customerData.state.trim(),
        postalCode: customerData.postalCode.trim(),
      };

      /*
       * Country should be ISO 2 letter format:
       * PK
       * US
       * GB
       *
       * Don't send empty country because API may reject it.
       */
      if (customerData.country.trim()) {
        payload.country = customerData.country
          .trim()
          .toUpperCase();
      }

      const response = await axios.put(
        `${GHL_BASE_URL}/contacts/${id}`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Version: GHL_API_VERSION,
            Accept: "application/json",
            "Content-Type": "application/json",
          },
        }
      );

      setMessage({
        status: true,
        reason: "success",
        desc: "Customer updated successfully in GoHighLevel.",
      });

      /*
       * GHL usually returns updated contact.
       * Update form again from response if available.
       */
      const updatedContact = response.data?.contact;

      if (updatedContact) {
        setCustomerData((prev) => ({
          ...prev,

          firstName:
            updatedContact.firstName ??
            prev.firstName,

          lastName:
            updatedContact.lastName ??
            prev.lastName,

          email:
            updatedContact.email ??
            prev.email,

          phone:
            updatedContact.phone ??
            prev.phone,

          address1:
            updatedContact.address1 ??
            prev.address1,

          city:
            updatedContact.city ??
            prev.city,

          state:
            updatedContact.state ??
            prev.state,

          postalCode:
            updatedContact.postalCode ??
            prev.postalCode,

          country:
            updatedContact.country ??
            prev.country,
        }));
      }
    } catch (error) {
      console.error(
        "Error updating GHL customer:",
        error.response?.data || error
      );

      let errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Failed to update customer.";

      /*
       * Sometimes GHL validation errors come
       * in another property.
       */
      if (error.response?.data?.errors) {
        try {
          errorMessage =
            typeof error.response.data.errors === "string"
              ? error.response.data.errors
              : JSON.stringify(
                  error.response.data.errors
                );
        } catch (err) {
          // ignore stringify error
        }
      }

      setMessage({
        status: true,
        reason: "error",
        desc: `Failed to update customer: ${errorMessage}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /*
   * ==========================================
   * LOADING SCREEN
   * ==========================================
   */
  if (loading) {
    return (
      <MasterLayout>
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "400px" }}
        >
          <div className="text-center">
            <div
              className="spinner-border text-primary"
              role="status"
            >
              <span className="visually-hidden">
                Loading...
              </span>
            </div>

            <p className="mt-3 mb-0">
              Loading customer information...
            </p>
          </div>
        </div>
      </MasterLayout>
    );
  }

  return (
    <MasterLayout>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-12">
            <div className="card shadow-sm border-0">

              {/* ============================= */}
              {/* HEADER */}
              {/* ============================= */}

              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                <div>
                  <h6 className="h6 mb-1">
                    Edit Customer
                  </h6>

                  <small className="text-muted">
                    GHL Contact ID: {id}
                  </small>
                </div>

                <button
                  type="button"
                  className="btn btn-light"
                  onClick={() => navigate(-1)}
                >
                  <Icon
                    icon="mdi:arrow-left"
                    className="me-1"
                  />
                  Back
                </button>
              </div>

              <div className="card-body">

                {/* ============================= */}
                {/* MESSAGE */}
                {/* ============================= */}

                {message.status && (
                  <div
                    className={`alert ${
                      message.reason === "error"
                        ? "alert-danger"
                        : "alert-success"
                    } alert-dismissible fade show mb-4`}
                    role="alert"
                  >
                    {message.desc}

                    <button
                      type="button"
                      className="btn-close"
                      onClick={() =>
                        setMessage((prev) => ({
                          ...prev,
                          status: false,
                        }))
                      }
                      aria-label="Close"
                    />
                  </div>
                )}

                {/* ============================= */}
                {/* FORM */}
                {/* ============================= */}

                <form onSubmit={handleSubmit}>
                  <div className="p-3 rounded">
                    <div className="row g-3">

                      {/* FIRST NAME */}

                      <div className="col-md-6">
                        <label
                          htmlFor="firstName"
                          className="form-label fw-bold"
                        >
                          First Name
                        </label>

                        <input
                          type="text"
                          id="firstName"
                          value={customerData.firstName}
                          onChange={(e) =>
                            handleInputChange(
                              "firstName",
                              e.target.value
                            )
                          }
                          className={`form-control form-control-lg ${
                            errors.firstName
                              ? "is-invalid"
                              : ""
                          }`}
                        />

                        <div className="form-text">
                          Customer's first name.
                        </div>

                        {errors.firstName && (
                          <div className="invalid-feedback">
                            {errors.firstName}
                          </div>
                        )}
                      </div>

                      {/* LAST NAME */}

                      <div className="col-md-6">
                        <label
                          htmlFor="lastName"
                          className="form-label fw-bold"
                        >
                          Last Name
                        </label>

                        <input
                          type="text"
                          id="lastName"
                          value={customerData.lastName}
                          onChange={(e) =>
                            handleInputChange(
                              "lastName",
                              e.target.value
                            )
                          }
                          className="form-control form-control-lg"
                        />

                        <div className="form-text">
                          Customer's last name.
                        </div>
                      </div>

                      {/* EMAIL */}

                      <div className="col-md-6">
                        <label
                          htmlFor="email"
                          className="form-label fw-bold"
                        >
                          Email
                        </label>

                        <input
                          type="email"
                          id="email"
                          value={customerData.email}
                          onChange={(e) =>
                            handleInputChange(
                              "email",
                              e.target.value
                            )
                          }
                          className={`form-control form-control-lg ${
                            errors.email
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="customer@example.com"
                        />

                        <div className="form-text">
                          Email is optional.
                        </div>

                        {errors.email && (
                          <div className="invalid-feedback">
                            {errors.email}
                          </div>
                        )}
                      </div>

                      {/* PHONE */}

                      <div className="col-md-6">
                        <label
                          htmlFor="phone"
                          className="form-label fw-bold"
                        >
                          Phone
                        </label>

                        <input
                          type="text"
                          id="phone"
                          value={customerData.phone}
                          onChange={(e) =>
                            handleInputChange(
                              "phone",
                              e.target.value
                            )
                          }
                          className={`form-control form-control-lg ${
                            errors.phone
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="+92514923101"
                        />

                        <div className="form-text">
                          Prefer international format.
                        </div>

                        {errors.phone && (
                          <div className="invalid-feedback">
                            {errors.phone}
                          </div>
                        )}
                      </div>

                      {/* ADDRESS */}

                      <div className="col-md-6">
                        <label
                          htmlFor="address1"
                          className="form-label fw-bold"
                        >
                          Address
                        </label>

                        <input
                          type="text"
                          id="address1"
                          value={customerData.address1}
                          onChange={(e) =>
                            handleInputChange(
                              "address1",
                              e.target.value
                            )
                          }
                          className="form-control form-control-lg"
                          placeholder="Enter address"
                        />

                        <div className="form-text">
                          Customer's street address.
                        </div>
                      </div>

                      {/* CITY */}

                      <div className="col-md-6">
                        <label
                          htmlFor="city"
                          className="form-label fw-bold"
                        >
                          City
                        </label>

                        <input
                          type="text"
                          id="city"
                          value={customerData.city}
                          onChange={(e) =>
                            handleInputChange(
                              "city",
                              e.target.value
                            )
                          }
                          className="form-control form-control-lg"
                          placeholder="City"
                        />
                      </div>

                      {/* STATE */}

                      <div className="col-md-6">
                        <label
                          htmlFor="state"
                          className="form-label fw-bold"
                        >
                          State
                        </label>

                        <input
                          type="text"
                          id="state"
                          value={customerData.state}
                          onChange={(e) =>
                            handleInputChange(
                              "state",
                              e.target.value
                            )
                          }
                          className="form-control form-control-lg"
                          placeholder="State / Province"
                        />
                      </div>

                      {/* POSTCODE */}

                      <div className="col-md-6">
                        <label
                          htmlFor="postalCode"
                          className="form-label fw-bold"
                        >
                          Postcode
                        </label>

                        <input
                          type="text"
                          id="postalCode"
                          value={customerData.postalCode}
                          onChange={(e) =>
                            handleInputChange(
                              "postalCode",
                              e.target.value
                            )
                          }
                          className="form-control form-control-lg"
                          placeholder="54000"
                        />

                        <div className="form-text">
                          Customer's postal code.
                        </div>
                      </div>

                      {/* COUNTRY */}

                      <div className="col-md-6">
                        <label
                          htmlFor="country"
                          className="form-label fw-bold"
                        >
                          Country
                        </label>

                        <input
                          type="text"
                          id="country"
                          maxLength={2}
                          value={customerData.country}
                          onChange={(e) =>
                            handleInputChange(
                              "country",
                              e.target.value.toUpperCase()
                            )
                          }
                          className={`form-control form-control-lg ${
                            errors.country
                              ? "is-invalid"
                              : ""
                          }`}
                          placeholder="PK"
                        />

                        <div className="form-text">
                          ISO country code, e.g. PK, US,
                          GB.
                        </div>

                        {errors.country && (
                          <div className="invalid-feedback">
                            {errors.country}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* ============================= */}
                  {/* BUTTONS */}
                  {/* ============================= */}

                  <div className="d-flex justify-content-between align-items-center mt-5">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-lg"
                      onClick={() => navigate(-1)}
                    >
                      <Icon
                        icon="mdi:arrow-left"
                        className="me-2"
                      />
                      Cancel
                    </button>

                    <button
                      type="submit"
                      className="btn btn-success btn-lg"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <>
                          <span
                            className="spinner-border spinner-border-sm me-2"
                            role="status"
                            aria-hidden="true"
                          />
                          Updating...
                        </>
                      ) : (
                        <>
                          <Icon
                            icon="simple-line-icons:check"
                            className="me-2"
                          />
                          Update Customer
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
};

export default EditCustomerPage;